import axios, { AxiosError, type AxiosInstance, type InternalAxiosRequestConfig } from 'axios';
import setupAxios from '@nextories/vue-core-components/src/utils/modules/axios';
import tokenStorage from './tokenStorage';

// Public endpoints: a 401 there is an answer, not an expired session
const PUBLIC_PATHS = ['login', 'logout', 'refreshToken', 'resetPasswordRequest', 'resetPassword'];

type RetriedRequest = InternalAxiosRequestConfig & { retried?: boolean };

let onSessionLost: () => void = () => {};
let refreshing: Promise<string> | null = null;

export function setSessionLostHandler(handler: () => void) {
  onSessionLost = handler;
}

function refreshAccess(baseURL: string): Promise<string> {
  const refresh = tokenStorage.getRefresh();
  if (!refresh) {
    return Promise.reject(new Error('No refresh token'));
  }
  // A bare axios call: the refresh request must not go through the interceptors below
  return axios.post<{ access: string }>('refreshToken', { refresh }, { baseURL })
    .then(({ data }) => {
      tokenStorage.setAccess(data.access);
      return data.access;
    });
}

/**
 * Axios instance of the api/one endpoints: the core instance, plus the bearer token
 * and one refresh of the access token when the API answers 401.
 */
export default function createHttp(baseURL: string): AxiosInstance {
  const http: AxiosInstance = setupAxios(baseURL);

  http.interceptors.request.use((config) => {
    const access = tokenStorage.getAccess();
    if (access && !config.headers.Authorization) {
      config.headers.Authorization = `Bearer ${access}`;
    }
    return config;
  });

  http.interceptors.response.use((response) => response, async (error: AxiosError) => {
    const request = error.config as RetriedRequest | undefined;
    const isPublic = PUBLIC_PATHS.includes(request?.url || '');
    if (error.response?.status !== 401 || !request || isPublic) {
      throw error;
    }
    if (!request.retried && tokenStorage.getRefresh()) {
      request.retried = true;
      try {
        refreshing = refreshing || refreshAccess(baseURL);
        const access = await refreshing;
        request.headers.Authorization = `Bearer ${access}`;
        return http(request);
      } catch {
        // Expired or revoked refresh token: the session is lost
      } finally {
        refreshing = null;
      }
    }
    tokenStorage.clear();
    onSessionLost();
    throw error;
  });

  return http;
}
